/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { GiftOptions as GiftOptionsComponent } from '@/cart/components';
import { Item } from '@/cart/data/models';
import { Container, Slot, SlotProps } from '@adobe-commerce/elsie/lib';
import { useGiftOptions } from '@/cart/hooks/useGiftOptions';
import {
  GiftOptionsViewProps,
  GiftOptionsDataSourcesProps,
  GiftFormDataType,
  ProductGiftOptionsConfig,
  GiftOptionsReadOnlyViewProps,
} from '@/cart/types';
import { ImageNodeRenderProps, ImageProps } from '@adobe-commerce/elsie/components';

export interface GiftOptionsProps {
  item: Item | ProductGiftOptionsConfig;
  view?: GiftOptionsViewProps;
  readOnlyFormOrderView: GiftOptionsReadOnlyViewProps;
  dataSource?: GiftOptionsDataSourcesProps;
  isEditable?: boolean;
  initialLoading?: boolean;
  handleItemsLoading?: (uid: string, state: boolean) => void;
  handleItemsError?: (uid: string, message?: string) => void;
  onItemUpdate?: ({ item }: { item: Item }) => void;
  onGiftOptionsChange?: (data: GiftFormDataType) => void;
  slots?: {
    SwatchImage?: SlotProps<{ 
      item: Item | ProductGiftOptionsConfig
      imageSwatchContext: ImageNodeRenderProps['imageSwatchContext']
      defaultImageProps: ImageProps
    }>;
  };
}

export const GiftOptions: Container<GiftOptionsProps> = ({
  item,
  view = 'order',
  readOnlyFormOrderView = 'primary',
  dataSource = 'cart',
  isEditable = true,
  initialLoading = true,
  slots,
  handleItemsLoading,
  handleItemsError,
  onItemUpdate,
  onGiftOptionsChange,
}: GiftOptionsProps) => {
  const {
    isGiftMessageVisible,
    areGiftOptionsVisible,
    loading,
    giftOptions,
    showModal,
    errorsField,
    updateLoading,
    cartData,
    isGiftOptionsApplied,
    fieldsDisabled,
    giftWrappingConfig,
    handleFormMouseLeave,
    updateGiftOptions,
    setShowModal,
    onInputChange,
    handleBlur,
    isGiftOptionsHidden,
  } = useGiftOptions({
    item,
    view,
    dataSource,
    initialLoading,
    handleItemsLoading,
    handleItemsError,
    onItemUpdate,
    onGiftOptionsChange,
  });

  if (isGiftOptionsHidden) {
    return null;
  }

  return (
    <GiftOptionsComponent
      item={item}
      view={view}
      loading={loading}
      onBlur={handleBlur}
      giftOptions={giftOptions}
      showModal={showModal}
      isEditable={isEditable}
      errorsField={errorsField}
      setShowModal={setShowModal}
      updateLoading={updateLoading}
      updateGiftOptions={updateGiftOptions}
      cartData={cartData}
      isGiftOptionsApplied={isGiftOptionsApplied}
      fieldsDisabled={fieldsDisabled}
      giftWrappingConfig={giftWrappingConfig}
      handleFormMouseLeave={handleFormMouseLeave}
      readOnlyFormOrderView={readOnlyFormOrderView}
      onInputChange={onInputChange}
      isGiftMessageVisible={isGiftMessageVisible}
      areGiftOptionsVisible={areGiftOptionsVisible}
      imageSwatchImageNode={
        slots?.SwatchImage ? ({ imageSwatchContext, ...defaultImageProps }) => (
          // No need to provide default content, as the slot will only be rendered if it's actually provided.
          <Slot 
            name="SwatchImage"
            slotTag='span'
            contentTag='span'
            slot={slots.SwatchImage} 
            context={{ item, imageSwatchContext, defaultImageProps }} 
          />
        ) : undefined
      }
    />
  );
};
